import { useRef, useState } from 'react'
import {
  Upload, Download, CheckCircle, XCircle, Loader2, FileSpreadsheet,
  AlertTriangle, RefreshCw, Link2, Users, ChevronRight, Eye, ArrowLeft,
} from 'lucide-react'
import { studentsApi, parentsApi, relationsApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

// ─── CSV templates matching actual backend columns ────────────────────────────
const TEMPLATES = {
  student: {
    headers: ['firstName', 'middleName', 'lastName', 'email', 'mobile', 'password', 'role', 'dob', 'gender', 'admissionNo', 'class'],
    rows: [
      ['Riya', '', 'Sharma', 'riya@example.com', '9876543210', 'School@123', 'student', '2010-05-15', 'female', 'ADM-001', 'CLASS 10 A'],
      ['Arjun', '', 'Patel', 'arjun@example.com', '9876543211', 'School@123', 'student', '2010-08-20', 'male', 'ADM-002', 'CLASS 10 A'],
    ],
    note: 'class must match exactly (e.g. "CLASS 10 A"). Leave middleName blank if not applicable.',
  },
  teacher: {
    headers: ['firstName', 'middleName', 'lastName', 'email', 'mobile', 'password', 'role', 'dob', 'gender'],
    rows: [
      ['Priya', '', 'Mehta', 'priya@school.com', '9876543212', 'School@123', 'teacher', '1985-03-10', 'female'],
      ['Rahul', '', 'Singh', 'rahul@school.com', '9876543213', 'School@123', 'teacher', '1990-07-22', 'male'],
    ],
    note: 'dob format: YYYY-MM-DD',
  },
  parent: {
    headers: ['firstName', 'middleName', 'lastName', 'email', 'mobile', 'password', 'role', 'dob', 'gender'],
    rows: [
      ['Suresh', '', 'Patel', 'suresh.parent@example.com', '9000000001', 'School@123', 'parent', '1975-01-15', 'male'],
      ['Anita', '', 'Singh', 'anita.parent@example.com', '9000000002', 'School@123', 'parent', '1978-09-30', 'female'],
    ],
    note: 'After creating parents, use the "Link Relations" tab to link them to students.',
  },
}

// ─── Relation CSV template ────────────────────────────────────────────────────
const RELATION_TEMPLATE = {
  headers: ['parentEmail', 'parentMobile', 'studentAdmissionNo', 'relation'],
  rows: [
    ['suresh.parent@example.com', '9000000001', 'ADM-001', 'father'],
    ['anita.parent@example.com', '9000000002', 'ADM-001', 'mother'],
    ['suresh.parent@example.com', '9000000001', 'ADM-002', 'guardian'],
  ],
  note: 'relation: father, mother, or guardian. Parents and students must already exist.',
}

// ─── Column info ──────────────────────────────────────────────────────────────
const USER_COLUMNS = [
  { col: 'firstName',   req: true,  note: 'First name' },
  { col: 'middleName',  req: false, note: 'Optional middle name' },
  { col: 'lastName',    req: true,  note: 'Last name' },
  { col: 'email',       req: true,  note: 'Unique email address' },
  { col: 'mobile',      req: true,  note: '10-digit mobile number' },
  { col: 'password',    req: true,  note: 'Minimum 6 characters' },
  { col: 'role',        req: true,  note: 'student / teacher / parent' },
  { col: 'dob',         req: true,  note: 'YYYY-MM-DD format' },
  { col: 'gender',      req: false, note: 'male / female / other' },
  { col: 'admissionNo', req: false, note: 'Required for students' },
  { col: 'class',       req: false, note: 'Required for students, e.g. CLASS 10 A' },
]

const RELATION_COLUMNS = [
  { col: 'studentAdmissionNo', req: true,  note: 'Existing student admission number' },
  { col: 'parentEmail',        req: false, note: 'Use when parent exists with email' },
  { col: 'parentMobile',       req: false, note: 'Use when parent exists with mobile' },
  { col: 'relation',           req: false, note: 'father / mother / guardian' },
]

const ROLE_COLORS = {
  student: { bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-500'  },
  teacher: { bg: 'bg-cyan-50',   text: 'text-cyan-700',   dot: 'bg-cyan-500'   },
  parent:  { bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  admin:   { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500' },
}

// ─── Utility ──────────────────────────────────────────────────────────────────
function downloadCSV(headers, rows, filename) {
  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function parseCSVText(text) {
  const lines = text.trim().split('\n').filter(Boolean)
  if (lines.length < 2) return { headers: [], rows: [] }
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^\uFEFF/, ''))
  const rows = lines.slice(1).map((line) => {
    const cols = line.split(',').map((c) => c.trim())
    return Object.fromEntries(headers.map((h, i) => [h, cols[i] ?? '']))
  })
  return { headers, rows }
}

const ROLES = [
  { value: 'student', label: 'Students', color: 'border-green-300 bg-green-50 text-green-700' },
  { value: 'teacher', label: 'Teachers', color: 'border-cyan-300 bg-cyan-50 text-cyan-700' },
  { value: 'parent',  label: 'Parents',  color: 'border-amber-300 bg-amber-50 text-amber-700' },
]

const TABS = [
  { id: 'upload',    label: 'Upload Users',  icon: Upload },
  { id: 'relations', label: 'Link Relations', icon: Link2 },
]

// ─── Step indicator ────────────────────────────────────────────────────────────
function StepIndicator({ step, steps }) {
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
            i + 1 < step ? 'bg-green-500 text-white' :
            i + 1 === step ? 'bg-primary-600 text-white' :
            'bg-gray-200 text-gray-400'
          }`}>
            {i + 1 < step ? '✓' : i + 1}
          </div>
          <span className={`text-xs font-medium ${i + 1 === step ? 'text-gray-900' : 'text-gray-400'}`}>{s}</span>
          {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
        </div>
      ))}
    </div>
  )
}

// ─── Upload Users Tab ─────────────────────────────────────────────────────────
function UploadUsersTab() {
  const [role, setRole] = useState('student')
  const [file, setFile] = useState(null)
  const [step, setStep] = useState(1)         // 1=select, 2=preview, 3=result
  const [validating, setValidating] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState(null) // validate result
  const [result, setResult] = useState(null)   // upload result
  const [showColumns, setShowColumns] = useState(false)
  const fileRef = useRef(null)

  const tpl = TEMPLATES[role]

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.name.endsWith('.csv')) { toast.error('Please upload a CSV file'); return }
    setFile(f)
    setPreview(null)
    setResult(null)
    setStep(1)
  }

  // ── Step 2: Validate ───────────────────────────────────────────────────────
  const handleValidate = async () => {
    if (!file) { toast.error('Select a CSV file first'); return }
    try {
      setValidating(true)
      const form = new FormData()
      form.append('file', file)
      const res = await studentsApi.bulkValidate(form)
      const rows = res?.preview ?? res?.rows ?? []
      const errors = res?.errors ?? []
      setPreview({ rows, errors, total: res?.total ?? rows.length })
      setStep(2)
    } catch (err) {
      // If backend doesn't support validate endpoint, skip to upload directly
      if (err.response?.status === 404 || err.response?.status === 405) {
        handleUpload()
      } else {
        toast.error(err.response?.data?.message || 'Validation failed')
      }
    } finally {
      setValidating(false)
    }
  }

  // ── Step 3: Upload ─────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) return
    try {
      setUploading(true)
      const form = new FormData()
      form.append('file', file)
      const res = await studentsApi.bulkUpload(form)
      setResult({
        created: res?.success ?? res?.created ?? res?.inserted ?? 0,
        failed:  res?.failed ?? res?.errors?.length ?? 0,
        errors:  res?.errors ?? [],
        users:   res?.users ?? res?.data ?? [],
      })
      setStep(3)
      toast.success('Upload complete')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const reset = () => {
    setFile(null)
    setPreview(null)
    setResult(null)
    setStep(1)
    if (fileRef.current) fileRef.current.value = ''
  }

  const roleColors = ROLE_COLORS[role] || ROLE_COLORS.student

  return (
    <div className="space-y-5">
      {/* Step indicator */}
      <div className="card p-4">
        <StepIndicator step={step} steps={['Select File', 'Preview', 'Result']} />
      </div>

      {/* ── STEP 1: Select + template ── */}
      {step === 1 && (
        <>
          {/* Role selector */}
          <div className="card p-5 space-y-4">
            <p className="text-sm font-semibold text-gray-700">Step 1 — Select type</p>
            <div className="flex gap-3">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => { setRole(r.value); reset() }}
                  className={`flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-colors ${
                    role === r.value ? r.color + ' border-current' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Template download + column guide */}
          <div className="card p-5 space-y-3">
            <p className="text-sm font-semibold text-gray-700">Step 2 — Download template</p>
            <p className="text-xs text-gray-500">
              Download the CSV template, fill your data row by row, then upload below.
              Do not change the column headers.
            </p>
            <button
              onClick={() => downloadCSV(tpl.headers, tpl.rows, `${role}_upload_template.csv`)}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" /> Download {role} template.csv
            </button>

            {/* Column guide toggle */}
            <button
              onClick={() => setShowColumns((v) => !v)}
              className="flex items-center gap-2 text-xs font-semibold text-primary-600 hover:text-primary-700"
            >
              <Eye className="w-3.5 h-3.5" />
              {showColumns ? 'Hide' : 'Show'} column guide
            </button>

            {showColumns && (
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-3">Column reference</p>
                {USER_COLUMNS.map((c) => (
                  <div key={c.col} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                    <span className="font-mono text-xs bg-white border border-gray-200 px-2 py-0.5 rounded-md text-gray-700 flex-shrink-0">{c.col}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${c.req ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                      {c.req ? 'Required' : 'Optional'}
                    </span>
                    <span className="text-xs text-gray-500">{c.note}</span>
                  </div>
                ))}
                {tpl.note && <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-2 py-1.5 mt-2">{tpl.note}</p>}
              </div>
            )}
          </div>

          {/* File picker */}
          <div className="card p-5 space-y-4">
            <p className="text-sm font-semibold text-gray-700">Step 3 — Upload your CSV</p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
            {!file ? (
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-3 hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
              >
                <FileSpreadsheet className="w-10 h-10 text-gray-300" />
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Click to select CSV file</p>
                  <p className="text-xs text-gray-400 mt-0.5">Only .csv files are accepted</p>
                </div>
              </button>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <FileSpreadsheet className="w-8 h-8 text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button onClick={reset} className="text-gray-400 hover:text-gray-600">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            )}
            <button
              onClick={handleValidate}
              disabled={!file || validating}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {validating
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Validating…</>
                : <><Eye className="w-4 h-4" /> Preview before upload</>
              }
            </button>
          </div>
        </>
      )}

      {/* ── STEP 2: Preview ── */}
      {step === 2 && preview && (
        <>
          <div className="card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700">Preview — {preview.total} rows found</p>
              <button onClick={() => setStep(1)} className="btn-secondary flex items-center gap-2 text-xs py-1.5 px-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Change file
              </button>
            </div>

            {/* Validation errors */}
            {preview.errors?.length > 0 && (
              <div className="rounded-xl bg-red-50 border border-red-100 p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-red-700">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {preview.errors.length} validation issue{preview.errors.length !== 1 ? 's' : ''} found
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {preview.errors.slice(0, 20).map((e, i) => (
                    <p key={i} className="text-xs text-red-600">
                      {typeof e === 'string' ? e : `Row ${e.row || i + 2}: ${e.error || e.message || JSON.stringify(e)}`}
                    </p>
                  ))}
                  {preview.errors.length > 20 && (
                    <p className="text-xs text-red-400">…and {preview.errors.length - 20} more</p>
                  )}
                </div>
              </div>
            )}

            {/* Preview rows */}
            {preview.rows?.length > 0 && (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {preview.rows.slice(0, 30).map((row, i) => {
                  const rc = ROLE_COLORS[row.role] || ROLE_COLORS.student
                  const name = [row.firstName, row.middleName, row.lastName].filter(Boolean).join(' ')
                  const hasErrors = row.errors?.length > 0
                  return (
                    <div
                      key={i}
                      className={`rounded-xl border p-3 flex items-start gap-3 ${
                        hasErrors ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full ${rc.bg} flex items-center justify-center flex-shrink-0`}>
                        <span className={`text-xs font-bold ${rc.text}`}>
                          {(name || 'U')[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">{name || '—'}</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${rc.bg} ${rc.text}`}>
                            {row.role}
                          </span>
                          <span className="text-xs text-gray-400">Row {i + 2}</span>
                          {hasErrors && (
                            <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Error</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{row.email || ''} {row.class ? `· ${row.class}` : ''}</p>
                        {hasErrors && row.errors.map((e, j) => (
                          <p key={j} className="text-xs text-red-600 mt-0.5">⚠ {e}</p>
                        ))}
                      </div>
                    </div>
                  )
                })}
                {preview.rows.length > 30 && (
                  <p className="text-xs text-gray-400 text-center py-2">…and {preview.rows.length - 30} more rows</p>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {uploading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                  : <><Upload className="w-4 h-4" /> Confirm & Upload {preview.total} rows</>
                }
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── STEP 3: Result ── */}
      {step === 3 && result && (
        <div className="card p-5 space-y-4">
          <div className="flex flex-col items-center py-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${result.failed === 0 ? 'bg-green-50' : 'bg-amber-50'}`}>
              {result.failed === 0
                ? <CheckCircle className="w-8 h-8 text-green-500" />
                : <AlertTriangle className="w-8 h-8 text-amber-500" />
              }
            </div>
            <p className="text-lg font-bold text-gray-900">Upload {result.failed === 0 ? 'Complete' : 'Partial'}</p>
            <p className="text-sm text-gray-500 mt-1">
              {result.created} added{result.failed > 0 ? `, ${result.failed} failed` : ''}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
              <div>
                <p className="text-2xl font-extrabold text-green-600">{result.created}</p>
                <p className="text-xs text-green-700">Successfully added</p>
              </div>
            </div>
            <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-2xl font-extrabold text-red-500">{result.failed}</p>
                <p className="text-xs text-red-600">Failed / skipped</p>
              </div>
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-orange-600">
                <AlertTriangle className="w-3.5 h-3.5" /> Errors
              </div>
              <div className="bg-orange-50 rounded-xl p-3 space-y-1 max-h-48 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-orange-700">
                    {typeof e === 'string' ? e : `Row ${e.row || i + 2}: ${e.error || e.message || JSON.stringify(e)}`}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Uploaded users preview */}
          {result.users?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500">Created accounts</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {result.users.slice(0, 20).map((u, i) => {
                  const rc = ROLE_COLORS[u.role] || ROLE_COLORS.student
                  const name = u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email
                  return (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                      <div className={`w-7 h-7 rounded-full ${rc.bg} flex items-center justify-center`}>
                        <span className={`text-xs font-bold ${rc.text}`}>{(name || 'U')[0]?.toUpperCase()}</span>
                      </div>
                      <span className="text-sm text-gray-700 flex-1">{name}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${rc.bg} ${rc.text}`}>{u.role}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <button onClick={reset} className="btn-secondary text-sm w-full">Upload another file</button>
        </div>
      )}
    </div>
  )
}

// ─── Link Relations Tab ───────────────────────────────────────────────────────
function LinkRelationsTab() {
  const [file, setFile] = useState(null)
  const [step, setStep] = useState(1)         // 1=select, 2=preview, 3=result
  const [previewing, setPreviewing] = useState(false)
  const [linking, setLinking] = useState(false)
  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)
  const [showColumns, setShowColumns] = useState(false)
  const fileRef = useRef(null)

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.name.endsWith('.csv')) { toast.error('Please upload a CSV file'); return }
    setFile(f)
    setPreview(null)
    setResult(null)
    setStep(1)
  }

  // ── Step 2: Build preview locally (resolve emails/admNo to names) ──────────
  const handlePreview = async () => {
    if (!file) { toast.error('Select a CSV file first'); return }
    try {
      setPreviewing(true)
      const text = await file.text()
      const { rows } = parseCSVText(text)
      if (rows.length === 0) { toast.error('CSV is empty or invalid'); return }

      const [allParents, allStudents] = await Promise.all([
        parentsApi.getAll({ limit: 5000 }),
        studentsApi.getAll({ limit: 5000 }),
      ])

      const parentByEmail = {}
      const parentByMobile = {}
      ;(Array.isArray(allParents) ? allParents : []).forEach((p) => {
        if (p.email)  parentByEmail[p.email.toLowerCase()] = p
        if (p.mobile) parentByMobile[p.mobile.trim()]     = p
      })

      const studentByAdmNo = {}
      ;(Array.isArray(allStudents) ? allStudents : []).forEach((s) => {
        if (s.admissionNo) studentByAdmNo[s.admissionNo.trim()] = s
      })

      const previewRows = rows.map((row, i) => {
        const rowNum = i + 2
        const parentEmail  = row.parentEmail?.trim().toLowerCase()
        const parentMobile = row.parentMobile?.trim()
        const admNo        = row.studentAdmissionNo?.trim()
        const relation     = row.relation?.trim().toLowerCase() || 'guardian'
        const errors = []

        if (!admNo) errors.push(`studentAdmissionNo is required`)

        const parent  = (parentEmail && parentByEmail[parentEmail]) || (parentMobile && parentByMobile[parentMobile]) || null
        const student = admNo ? studentByAdmNo[admNo] : null

        if (!parent && (parentEmail || parentMobile))
          errors.push(`Parent not found (email: ${parentEmail || '—'}, mobile: ${parentMobile || '—'})`)
        else if (!parent)
          errors.push('parentEmail or parentMobile is required')

        if (admNo && !student) errors.push(`Student not found (admNo: ${admNo})`)

        if (relation && !['father', 'mother', 'guardian'].includes(relation))
          errors.push(`relation must be father, mother, or guardian`)

        const parentName = parent ? (parent.name || `${parent.firstName || ''} ${parent.lastName || ''}`.trim()) : null
        const studentName = student ? (student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim()) : null

        return {
          rowNum,
          parent, student,
          parentName: parentName || parentEmail || parentMobile || '—',
          studentName: studentName || admNo || '—',
          relation: relation || 'guardian',
          errors,
          valid: errors.length === 0,
        }
      })

      setPreview({ rows: previewRows, total: rows.length, valid: previewRows.filter((r) => r.valid).length })
      setStep(2)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preview failed')
    } finally {
      setPreviewing(false)
    }
  }

  // ── Step 3: Link ───────────────────────────────────────────────────────────
  const handleLink = async () => {
    if (!preview) return
    const validLinks = preview.rows.filter((r) => r.valid)
    if (validLinks.length === 0) { toast.error('No valid rows to link'); return }
    try {
      setLinking(true)
      let success = 0
      const errors = []
      for (const row of validLinks) {
        try {
          await relationsApi.link({
            studentId: row.student._id || row.student.id,
            parentId:  row.parent._id || row.parent.id,
            relation:  row.relation,
          })
          success++
        } catch (err) {
          errors.push(`${row.parentName} → ${row.studentName}: ${err.response?.data?.message || err.message}`)
        }
      }
      setResult({ success, failed: validLinks.length - success, skipped: preview.total - validLinks.length, errors })
      setStep(3)
      toast.success('Linking complete')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Linking failed')
    } finally {
      setLinking(false)
    }
  }

  const reset = () => {
    setFile(null)
    setPreview(null)
    setResult(null)
    setStep(1)
    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <div className="space-y-5">
      {/* Step indicator */}
      <div className="card p-4">
        <StepIndicator step={step} steps={['Select File', 'Preview', 'Result']} />
      </div>

      {/* ── STEP 1 ── */}
      {step === 1 && (
        <>
          <div className="card p-5 bg-indigo-50/60 border border-indigo-100 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
              <Link2 className="w-4 h-4" /> How it works
            </div>
            <p className="text-xs text-indigo-600">
              Upload a CSV to link existing parents to their children.
              Parents and students must already be in the system.
            </p>
          </div>

          <div className="card p-5 space-y-3">
            <p className="text-sm font-semibold text-gray-700">Step 1 — Download template</p>
            <button
              onClick={() => downloadCSV(RELATION_TEMPLATE.headers, RELATION_TEMPLATE.rows, 'parent_student_relations.csv')}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" /> Download relations template.csv
            </button>

            <button
              onClick={() => setShowColumns((v) => !v)}
              className="flex items-center gap-2 text-xs font-semibold text-primary-600 hover:text-primary-700"
            >
              <Eye className="w-3.5 h-3.5" /> {showColumns ? 'Hide' : 'Show'} column guide
            </button>

            {showColumns && (
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-100">
                {RELATION_COLUMNS.map((c) => (
                  <div key={c.col} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                    <span className="font-mono text-xs bg-white border border-gray-200 px-2 py-0.5 rounded-md text-gray-700 flex-shrink-0">{c.col}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${c.req ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                      {c.req ? 'Required' : 'Optional'}
                    </span>
                    <span className="text-xs text-gray-500">{c.note}</span>
                  </div>
                ))}
                <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-2 py-1.5 mt-2">{RELATION_TEMPLATE.note}</p>
              </div>
            )}
          </div>

          <div className="card p-5 space-y-4">
            <p className="text-sm font-semibold text-gray-700">Step 2 — Upload your CSV</p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
            {!file ? (
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-3 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors"
              >
                <FileSpreadsheet className="w-10 h-10 text-gray-300" />
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Click to select CSV file</p>
                  <p className="text-xs text-gray-400 mt-0.5">Only .csv files are accepted</p>
                </div>
              </button>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <FileSpreadsheet className="w-8 h-8 text-indigo-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button onClick={reset} className="text-gray-400 hover:text-gray-600">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            )}
            <button
              onClick={handlePreview}
              disabled={!file || previewing}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {previewing
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading preview…</>
                : <><Eye className="w-4 h-4" /> Preview before linking</>
              }
            </button>
          </div>
        </>
      )}

      {/* ── STEP 2: Preview ── */}
      {step === 2 && preview && (
        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Preview — {preview.valid}/{preview.total} valid rows
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {preview.total - preview.valid} rows have errors and will be skipped
              </p>
            </div>
            <button onClick={() => setStep(1)} className="btn-secondary flex items-center gap-2 text-xs py-1.5 px-3">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          </div>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {preview.rows.map((row, i) => (
              <div
                key={i}
                className={`rounded-xl border p-3 ${row.valid ? 'border-gray-100 bg-gray-50' : 'border-red-200 bg-red-50'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${row.valid ? 'bg-green-50' : 'bg-red-100'}`}>
                    {row.valid
                      ? <CheckCircle className="w-4 h-4 text-green-500" />
                      : <XCircle className="w-4 h-4 text-red-400" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {row.parentName} <span className="text-gray-400">→</span> {row.studentName}
                    </p>
                    <p className="text-xs text-gray-500">
                      Row {row.rowNum} · {row.relation}
                    </p>
                    {row.errors.map((e, j) => (
                      <p key={j} className="text-xs text-red-600 mt-0.5">⚠ {e}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleLink}
              disabled={linking || preview.valid === 0}
              className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {linking
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Linking…</>
                : <><Link2 className="w-4 h-4" /> Link {preview.valid} valid rows</>
              }
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Result ── */}
      {step === 3 && result && (
        <div className="card p-5 space-y-4">
          <div className="flex flex-col items-center py-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${result.failed === 0 ? 'bg-green-50' : 'bg-amber-50'}`}>
              {result.failed === 0
                ? <CheckCircle className="w-8 h-8 text-green-500" />
                : <AlertTriangle className="w-8 h-8 text-amber-500" />
              }
            </div>
            <p className="text-lg font-bold text-gray-900">Linking {result.failed === 0 ? 'Complete' : 'Partial'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-extrabold text-green-600">{result.success}</p>
                <p className="text-xs text-green-700">Successfully linked</p>
              </div>
            </div>
            <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-2xl font-extrabold text-red-500">{result.failed + (result.skipped || 0)}</p>
                <p className="text-xs text-red-600">Failed / skipped</p>
              </div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-orange-600">
                <AlertTriangle className="w-3.5 h-3.5" /> Errors
              </div>
              <div className="bg-orange-50 rounded-xl p-3 space-y-1 max-h-48 overflow-y-auto">
                {result.errors.map((e, i) => <p key={i} className="text-xs text-orange-700">{e}</p>)}
              </div>
            </div>
          )}
          <button onClick={reset} className="btn-secondary text-sm w-full">Upload another file</button>
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BulkUploadPage() {
  const [activeTab, setActiveTab] = useState('upload')

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Bulk Upload</h2>
        <p className="text-sm text-gray-500 mt-0.5">Import users or create parent–student relations in bulk</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
              activeTab === id ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'upload' ? <UploadUsersTab /> : <LinkRelationsTab />}

      {/* Tips */}
      <div className="card p-5 bg-blue-50/50 border border-blue-100">
        <p className="text-xs font-semibold text-blue-700 mb-2">Tips</p>
        <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
          {activeTab === 'upload' ? <>
            <li>Do not modify column headers in the template</li>
            <li>The <code className="font-mono">role</code> column must be: student, teacher, or parent</li>
            <li>For students, <code className="font-mono">class</code> must match exactly (e.g. "CLASS 10 A")</li>
            <li>dob format: YYYY-MM-DD (e.g. 2010-05-15)</li>
            <li>Preview step will show you what will be created before committing</li>
            <li>Duplicate emails or admission numbers within your school will be skipped</li>
            <li>Max 500 rows per upload</li>
          </> : <>
            <li>Parents and students must already exist before linking</li>
            <li>Use parentEmail or parentMobile to identify the parent</li>
            <li>Use the student's exact admission number</li>
            <li>relation must be exactly: father, mother, or guardian</li>
            <li>Each student can have at most 2 parents linked</li>
            <li>Preview step validates all rows against real data before linking</li>
          </>}
        </ul>
      </div>
    </div>
  )
}
